'use es6';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { NavMarker } from 'react-rhumb';
import FormattedMessage from 'I18n/components/FormattedMessage';
import getLang from 'I18n/utils/getLang';
import unescapedText from 'I18n/utils/unescapedText';
import H1 from 'UIComponents/elements/headings/H1';
import UIListingPage from 'UIComponents/page/UIListingPage';
import UIFlex from 'UIComponents/layout/UIFlex';
import UIBreadcrumbs from 'UIComponents/nav/UIBreadcrumbs';
import UIRouterLink from 'ui-addon-react-router-dom/UIRouterLink';
import { GYPSUM, OLAF } from 'HubStyleTokens/colors';
import ReviewsHeader from 'marketplace-ui-reviews/components/ReviewsHeader';
import ReviewsFilterSortDropdowns from 'marketplace-ui-reviews/components/ReviewsFilterSortDropdowns';
import ReviewsPaginatedList from 'marketplace-ui-reviews/components/ReviewsPaginatedList';
import ReviewsListStats from 'marketplace-ui-reviews/components/ReviewsListStats';
import ReviewFilters from 'marketplace-ui-reviews/records/ReviewFilters';
import { REVIEWS_PER_PAGE, SEARCH_KEY } from 'marketplace-ui-reviews/constants/ReviewsListQueries';
import * as ReviewTypes from 'marketplace-ui-reviews/constants/ReviewType';
import { useReviewability } from 'marketplace-ui-reviews/hooks/useReviewability';
import * as ScreenSizes from 'ecosystem-marketplace-core/constants/layout/ScreenSizes';
import AllReviewsReviewRenderer from '../../components/detail/reviews/AllReviewsReviewRenderer';
import ReviewWizard from '../../components/detail/reviews/ReviewWizard';
import WriteReviewButton from '../../components/detail/reviews/WriteReviewButton';
import ReviewsListEmptyState from '../../components/detail/reviews/ReviewsListEmptyState';
import { DetailMainSection as MainSection } from 'ecosystem-marketplace-core/components/layout/MainSection';
import MainSectionInner from 'ecosystem-marketplace-core/components/layout/MainSectionInner';
import Header from '../../components/detail/Header';
import LoadingWrapper from 'ecosystem-marketplace-core/components/LoadingWrapper';
import { useResponsiveProps } from 'ecosystem-marketplace-core/hooks/useResponsiveProps';
import { getUsageTracker } from '../../utils/usageTracker';
import { getIsPrerenderUserAgent } from 'ecosystem-marketplace-core/utils/userAgent';
import { makeGetListingView } from '../../selectors/details';
import { PublicOnly, getIsCustomer } from '../../selectors/auth';
import { isSucceeded } from 'marketplace-ui-common/requestStates/RequestStateUtils';
import { useParams } from 'react-router-dom';
import { MINIMUM_REVIEWS_NEEDED_TO_DISPLAY } from '../../constants/reviews';
import { getIsPublic } from '../../selectors/auth';
import DocumentHead from 'ecosystem-marketplace-core/components/portals/DocumentHead';
import PrerenderReady from 'ecosystem-marketplace-core/components/prerender/PrerenderReady';
import useReviewsRequest from '../../utils/hooks/useReviewsRequest';
import useReviewFilters from '../../utils/hooks/useReviewFilters';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const REVIEWS_WIDTH = 900;
const EmptyLoadingComponent = () => /*#__PURE__*/_jsx("div", {});
const AllReviewsPage = () => {
  const {
    superCategory,
    category,
    slug
  } = useParams();
  const getListingView = useMemo(makeGetListingView, []);
  const {
    isMobileView
  } = useResponsiveProps({
    isMobileView: {
      [ScreenSizes.XS]: true,
      [ScreenSizes.MD]: false
    }
  });
  const isCustomer = useSelector(getIsCustomer);
  const isPrerenderUserAgent = getIsPrerenderUserAgent();
  const preferredLanguage = getLang();
  const listingView = useSelector(state => getListingView(state, {
    slug
  }));
  const isPublic = useSelector(getIsPublic);
  const [reviewSubmitted, setReviewSubmitted] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [pageViewTracked, setPageViewTracked] = useState(false);
  const tracker = getUsageTracker();
  const {
    name,
    iconUrl,
    id,
    offeringId,
    offering: {
      credentials,
      homemade,
      portalId: offeringPortalId
    },
    tagline
  } = listingView;
  const {
    reviewFilters,
    setReviewFilters
  } = useReviewFilters(new ReviewFilters({
    reviewTypes: [ReviewTypes.APP],
    entityId: offeringId
  }));
  const {
    unreviewableReason,
    requestState: reviewabilityRequestStatus
  } = useReviewability({
    entityId: offeringId,
    entityPortalId: offeringPortalId,
    isPublicApp: isPublic,
    reviewPending: reviewSubmitted,
    reviewType: 'APP'
  });
  const {
    reviews,
    reviewStats,
    reviewsFetchStatus,
    reviewStatsFetchStatus,
    reviewsTotal,
    reviewsCount
  } = useReviewsRequest(reviewFilters, offeringId);
  const eventProperties = useMemo(() => Object.assign({}, listingView.getEventProperties(), {
    slug,
    screen: 'all reviews',
    preferredLanguage
  }), [listingView, preferredLanguage, slug]);
  const trackInteraction = useCallback((action, section, value, properties = {}) => {
    tracker.track('listing-detail-interaction', Object.assign({
      action,
      section,
      value
    }, properties, eventProperties));
  }, [tracker, eventProperties]);
  const onUpdateFilters = useCallback(({
    textKey,
    value
  }) => {
    if (textKey === SEARCH_KEY) {
      setSearchTerm(value);
    }
    setReviewFilters({
      textKey,
      value
    }, trackInteraction);
  }, [setReviewFilters, trackInteraction]);
  const documentTitle = useMemo(() => {
    return unescapedText('appMarketplace.detail.reviews.all.htmlTitle', {
      name
    });
  }, [name]);
  useEffect(() => {
    document.title = documentTitle;
    if (!pageViewTracked) {
      tracker.track('all-reviews-pageview', Object.assign({}, eventProperties));
      setPageViewTracked(true);
    }
  }, [documentTitle, eventProperties, pageViewTracked, tracker]);
  const renderedHeader = useMemo(() => {
    const breadcrumbs = /*#__PURE__*/_jsx(UIBreadcrumbs, {
      className: "m-bottom-1 m-top-0 p-left-0",
      children: /*#__PURE__*/_jsx(UIRouterLink, {
        to: `/${superCategory}/${category}/${slug}#reviews`,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "appMarketplace.detail.reviews.all.back",
          options: {
            name
          }
        })
      })
    });
    return /*#__PURE__*/_jsx(MainSectionInner, {
      children: /*#__PURE__*/_jsx(Header, {
        appId: id,
        appName: name,
        breadcrumbs: breadcrumbs,
        credentials: credentials,
        headingLevel: reviewsCount ? 'h2' : 'h1',
        homemade: homemade,
        iconUrl: iconUrl,
        isCustomer: isCustomer,
        showTabs: false,
        showConnectButton: false,
        showSetUpUrl: false,
        tagline: tagline,
        wrapperStyle: {
          paddingBottom: '24px'
        }
      })
    });
  }, [credentials, name, homemade, iconUrl, id, isCustomer, reviewsCount, tagline, superCategory, category, slug]);
  const {
    inline,
    overallDistClassName
  } = useResponsiveProps({
    inline: {
      xs: false,
      md: true
    },
    overallDistClassName: {
      xs: undefined,
      md: 'm-all-auto'
    }
  });
  const renderedReviewsListStats = useMemo(() => {
    if (reviewsCount && offeringId) {
      return /*#__PURE__*/_jsxs("div", {
        style: {
          maxWidth: REVIEWS_WIDTH,
          width: '100%'
        },
        children: [/*#__PURE__*/_jsxs(UIFlex, {
          align: "center",
          justify: "between",
          direction: isMobileView ? 'column' : 'row',
          children: [/*#__PURE__*/_jsx(H1, {
            "aria-level": "1",
            className: "m-top-10 m-bottom-8 is--heading-2",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "appMarketplace.detail.reviews.all.headingText",
              options: {
                name
              }
            })
          }), /*#__PURE__*/_jsx(WriteReviewButton, {
            className: !isMobileView && 'm-left-4',
            onClick: () => setWizardOpen(true),
            size: "md",
            unreviewableReason: unreviewableReason
          })]
        }), reviewsCount >= MINIMUM_REVIEWS_NEEDED_TO_DISPLAY && /*#__PURE__*/_jsx(ReviewsListStats, {
          className: "m-bottom-15",
          inline: inline,
          onUpdateFilters: onUpdateFilters,
          overallDistClassName: overallDistClassName,
          reviewStats: reviewStats,
          reviewType: ReviewTypes.APP,
          width: 300
        })]
      });
    } else {
      return /*#__PURE__*/_jsx("div", {});
    }
  }, [reviewsCount, offeringId, isMobileView, name, unreviewableReason, inline, onUpdateFilters, overallDistClassName, reviewStats]);
  const renderedReviewsListBody = useMemo(() => {
    if (reviewsCount) {
      return /*#__PURE__*/_jsxs("div", {
        style: {
          maxWidth: REVIEWS_WIDTH,
          width: '100%'
        },
        children: [/*#__PURE__*/_jsx(ReviewsHeader, {
          onUpdateFilters: onUpdateFilters,
          reviewsCount: reviewsTotal,
          reviewFilters: reviewFilters
        }), /*#__PURE__*/_jsx(ReviewsFilterSortDropdowns, {
          onUpdateFilters: onUpdateFilters,
          reviewFilters: reviewFilters,
          reviewStats: reviewStats
        }), /*#__PURE__*/_jsx(ReviewsPaginatedList, {
          onUpdateFilters: onUpdateFilters,
          maxWidth: REVIEWS_WIDTH,
          ReviewRenderer: AllReviewsReviewRenderer,
          reviews: reviews,
          reviewFilters: reviewFilters,
          reviewsFetchStatus: reviewsFetchStatus,
          reviewsTotal: reviewsTotal,
          searchTerm: searchTerm,
          showPagination: !isPrerenderUserAgent && reviewsTotal >= REVIEWS_PER_PAGE
        })]
      });
    } else {
      return /*#__PURE__*/_jsx("div", {
        className: "m-top-15",
        children: /*#__PURE__*/_jsx(ReviewsListEmptyState, {
          onWriteReviewClick: () => setWizardOpen(true),
          unreviewableReason: unreviewableReason
        })
      });
    }
  }, [reviewsCount, onUpdateFilters, reviewsTotal, reviewFilters, reviewStats, reviews, reviewsFetchStatus, searchTerm, isPrerenderUserAgent, unreviewableReason]);
  return /*#__PURE__*/_jsx(LoadingWrapper, {
    requestStatus: reviewabilityRequestStatus,
    children: /*#__PURE__*/_jsxs(NavMarker, {
      name: "DETAIL_REVIEWS_LOAD",
      children: [/*#__PURE__*/_jsx(PublicOnly, {
        children: /*#__PURE__*/_jsx(DocumentHead, {
          children: /*#__PURE__*/_jsx(PrerenderReady, {
            ready: isSucceeded(reviewsFetchStatus)
          })
        })
      }), /*#__PURE__*/_jsxs(UIListingPage, {
        contentAreaFlush: true,
        headerComponent: renderedHeader,
        mainSectionBackgroundColor: OLAF,
        pageBackgroundColor: GYPSUM,
        children: [/*#__PURE__*/_jsx(MainSection, {
          children: /*#__PURE__*/_jsxs(UIFlex, {
            direction: "column",
            align: "center",
            alignSelf: "center",
            className: "p-bottom-15",
            children: [/*#__PURE__*/_jsx(LoadingWrapper, {
              LoadingComponent: EmptyLoadingComponent,
              requestStatus: reviewStatsFetchStatus,
              children: renderedReviewsListStats
            }), renderedReviewsListBody]
          })
        }), wizardOpen && /*#__PURE__*/_jsx(ReviewWizard, {
          appName: name,
          appIcon: iconUrl,
          offeringId: offeringId,
          onClose: () => setWizardOpen(false),
          onSuccess: () => {
            setWizardOpen(false);
            setReviewSubmitted(true);
          },
          onTrackInteraction: trackInteraction,
          unreviewableReason: unreviewableReason
        })]
      })]
    })
  });
};
export default AllReviewsPage;